<script lang="ts">
    import { _ } from 'svelte-i18n';
    import { afterUpdate, onMount, tick } from 'svelte';

    import { debug, leftPageWidthPx, mainMenu, planning } from '../../../stores/store';
    import {
        AssignmentApi,
        type AssignmentResponseType,
        type ReorderedRouteType,
    } from '../Planning/AssignmentApi';
    import { api } from '../../../stores/api';
    import {
        type RoutePointDeltaType,
        type RoutePointExtendedType,
        RoutePointsApi,
    } from '../../utilities/RoutePointsApi';
    import PageGanttCouriersRow from './GanttCouriersRow.svelte';
    import { Assignment, AssignmentPart, AssignmentsData, AssignmentType } from './AssignmentsData';
    import Spinner from '../../shared/Spinner/Spinner.svelte';
    import { VehiclesApi } from '../Vehicles/VehiclesApi';
    import type { VehicleResponseType } from '../Vehicles/types';
    import { WarehouseApi, type WarehouseResponseType } from '../Warehouses/WarehouseApi';
    import { RouteInfoUtils } from '../Planning/PageDeliveries2/RouteSummaryApp/RouteInfoUtils';
    import ErrorInline from '../../shared/ErrorInline.svelte';
    import InfoInline from '../../shared/InfoInline.svelte';
    import { GanttCouriersSettings, GanttCouriersSettingsModeType } from './GanttCouriersSettings';
    import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
    import ButtonSmallBlue from '../../shared/ButtonSmallBlue.svelte';

    import reorderStackFile from './reorder_stack.json';
    import { ReorderStackUtils } from './ReorderStackUtils';
    import { GanttDumpApi } from './GanttDumpApi';
    import TemplateInner from '../../shared/TemplateInner.svelte';
    import { smartRounding } from '../telematics/tools/smartRounding';
    import { formatTime } from './formatTime';

    interface AssignmentWithRoutePoints {
        assignment: AssignmentResponseType;
        routePoints: RoutePointExtendedType[];
        vehicle: VehicleResponseType;
        warehouse: WarehouseResponseType | null;
    }

    export let ganttCouriersSettings: GanttCouriersSettings;

    let assignmentsData: AssignmentsData = new AssignmentsData();

    let assignmentsFromApi: AssignmentResponseType[] | null = null;
    let vehiclesFromApi: VehicleResponseType[] | null = null;
    let warehousesFromApi: WarehouseResponseType[] | null = null;
    let assignmentsWithRoutePoints: AssignmentWithRoutePoints[] = [];
    let isLoading = false;
    let error: string | null = null;

    let loadingMessage: string = $_('Loading Couriers Gantt');

    let timeModeLabel = 'Real-time mode';

    let timeRange: {
        start: string;
        end: string;
        hours: { time: string; position: number }[];
    } | null = null;
    let earliestStartTime: string | null = null;
    let earliestStartTimeRounded: string | null = null;
    let totalTimelineWidth: number = 0;

    function cleanup() {
        assignmentsData = new AssignmentsData();
        assignmentsFromApi = null;
        vehiclesFromApi = null;
        warehousesFromApi = null;
        assignmentsWithRoutePoints = [];

        error = null;
        earliestStartTime = null;
        earliestStartTimeRounded = null;
        ganttCouriersSettings.assignmentIdsLoaded = false;
    }

    function getWarehouseById(id: number): WarehouseResponseType | null {
        return warehousesFromApi?.find(warehouse => warehouse.id === id) ?? null;
    }

    async function loadAssignments() {
        if (error) {
            console.warn('PageGanttCouriers, loadAssignments, error already set', error);
            return;
        }
        if (ganttCouriersSettings.assignmentIds.length === 0 && !ganttCouriersSettings.useDumpApi) {
            console.warn(
                'PageGanttCouriers, loadAssignments, no assignments to load',
                ganttCouriersSettings
            );
            ganttCouriersSettings.assignmentIdsLoaded = true;
            return;
        }
        try {
            if (ganttCouriersSettings.useDumpApi) {
                assignmentsFromApi = await GanttDumpApi.getAssignments(
                    $api,
                    ganttCouriersSettings.getSelectedDay()
                );
            } else {
                assignmentsFromApi = await AssignmentApi.getWithQuery(
                    $api,
                    new ApiFilterBuilder(null),
                    {
                        id: { $in: ganttCouriersSettings.assignmentIds },
                        // route_info: 'not_null',
                    }
                );
            }
            console.log('gantt_dump, assignments', assignmentsFromApi);
            ganttCouriersSettings.assignmentIdsLoaded = true;
        } catch (err) {
            console.error('PageGanttCouriers, loadAssignments, error', err);
            error = 'Failed to load assignments';
        }
    }

    async function loadVehicles() {
        if (error) {
            console.warn('PageGanttCouriers, loadVehicles, error already set', error);
            return;
        }
        if (ganttCouriersSettings.useDumpApi) {
            console.log('PageGanttCouriers, loadVehicles, using file data');
            vehiclesFromApi = await GanttDumpApi.getVehicles(
                $api,
                ganttCouriersSettings.getSelectedDay()
            ).catch(err => {
                console.error('PageGanttCouriers, loadVehicles, error', err);
                error = 'Failed to load vehicles';
                return [];
            });
            console.log('gantt_dump, vehicles', vehiclesFromApi);
            return;
        } else {
            vehiclesFromApi = await VehiclesApi.getAll($api).catch(err => {
                console.error('PageGanttCouriers, loadVehicles, error', err);
                error = 'Failed to load vehicles';
                return [];
            });
        }
        console.log('gantt_dump, vehicles', vehiclesFromApi);
    }

    async function loadWarehouses() {
        if (error) {
            console.warn('PageGanttCouriers, loadWarehouses, error already set', error);
            return;
        }
        if (ganttCouriersSettings.useDumpApi) {
            console.log('PageGanttCouriers, loadWarehouses, using file data');
            warehousesFromApi = await GanttDumpApi.getWarehouses(
                $api,
                ganttCouriersSettings.getSelectedDay()
            ).catch(err => {
                console.error('PageGanttCouriers, loadWarehouses, error', err);
                error = 'Failed to load warehouses';
                return [];
            });
            console.log('gantt_dump, warehouses', warehousesFromApi);
            return;
        } else {
            warehousesFromApi = await WarehouseApi.getAll($api).catch(err => {
                console.error('PageGanttCouriers, loadWarehouses, error', err);
                error = 'Failed to load warehouses';
                return [];
            });
        }
        console.log('gantt_dump, warehouses', warehousesFromApi);
    }

    async function loadRoutes() {
        if (!assignmentsFromApi) {
            console.log('PageGanttCouriers, loadRoutes, assignmentsFromApi is null');
            return;
        }
        if (!vehiclesFromApi) {
            console.log('PageGanttCouriers, loadRoutes, vehiclesFromApi is null');
            return;
        }

        console.log('loadRoutes start', new Date());
        let loadRoutePointsPromises = [];
        for (let assignment of assignmentsFromApi) {
            if (
                assignment.route_info !== null &&
                assignment.route_info.progress &&
                assignment.route_info.progress.percent == 100
            ) {
                console.warn('loadRoutes accepted...', assignment.id);
                let vehicle = vehiclesFromApi.find(vehicle => vehicle.id === assignment.vehicle_id);
                if (!vehicle) {
                    console.warn(
                        'PageGanttCouriers, loadRoutes, vehicle not found for assignment',
                        assignment
                    );
                    continue;
                }
                // console.log(
                //     'GanttCouriers assignment.route_info.reorder_stack',
                //     assignment.route_info.reorder_stack
                // );
                let warehouse = null;
                if (vehicle && vehicle.warehouse_id) {
                    warehouse = getWarehouseById(vehicle.warehouse_id);
                }
                loadRoutePointsPromises.push(loadRoutePoints(assignment, vehicle, warehouse));
            } else {
                console.warn('loadRoutes ignored by cond', assignment.id);
            }
        }

        await Promise.all(loadRoutePointsPromises);
        console.log('loadRoutes done', new Date());

        processData();
    }

    async function loadRoutePoints(
        assignment: any,
        vehicle: VehicleResponseType,
        warehouse: WarehouseResponseType | null
    ) {
        console.log('loadRoutePoints', assignment);
        let assignmentId = assignment.id;
        console.log('loadRoutePoints start', assignmentId);
        if (ganttCouriersSettings.useDumpApi) {
            console.log('PageGanttCouriers, loadRoutePoints, using file data');
            let routePoints: RoutePointExtendedType[] = await GanttDumpApi.getPoints(
                $api,
                assignmentId,
                ganttCouriersSettings.getSelectedDay()
            );
            console.log('loadRoutePoints done', assignmentId);
            assignmentsWithRoutePoints = [
                ...assignmentsWithRoutePoints,
                {
                    assignment: assignment,
                    routePoints: routePoints,
                    vehicle: vehicle,
                    warehouse: warehouse,
                },
            ];
            return;
        } else {
            let routePoints: RoutePointExtendedType[] = await RoutePointsApi.getPoints(
                $api,
                assignmentId
            ).catch(err => {
                console.error('PageGanttCouriers, loadRoutePoints, error', err);
                error = 'Failed to load route points';
                return [];
            });
            console.log('loadRoutePoints done', assignmentId);
            assignmentsWithRoutePoints = [
                ...assignmentsWithRoutePoints,
                {
                    assignment: assignment,
                    routePoints: routePoints,
                    vehicle: vehicle,
                    warehouse: warehouse,
                },
            ];
            console.log('gantt_dump, routes end', assignment, routePoints);
        }
    }

    function getGanttAssignmentForRoutePointsPlanned(
        assignmentsWithRoutePoint: AssignmentWithRoutePoints
    ): Assignment {
        let assignmentId = assignmentsWithRoutePoint.assignment.id;
        let assignment = assignmentsWithRoutePoint.assignment;
        let routePoints = assignmentsWithRoutePoint.routePoints;
        let vehicle = assignmentsWithRoutePoint.vehicle;
        let warehouse = assignmentsWithRoutePoint.warehouse;

        let previousAddress = warehouse ? warehouse.name : 'Start';
        let previousTime = 0;
        let previousPoint = null;
        let ganttAssignment = new Assignment(
            assignmentId,
            vehicle.name,
            assignment.color || '#000000',
            assignment.start_time_route,
            GanttCouriersSettingsModeType.PLANNED
        );

        let routeDeliveryIterator = 1;

        // during route optimization delta of all points is null, normal route has only first point delta set to null
        if (!routePoints[1] || routePoints[1].delta === null) {
            console.log('Skipping assignment, no delta', assignmentId);
            return ganttAssignment;
        }

        for (let routePointIterator in routePoints) {
            let routePoint = routePoints[routePointIterator];
            if (routePoint.delta === null) {
                continue;
            }

            let currentAddress = '';
            if (routePoint.jobs) {
                currentAddress =
                    (routePoint.address_geocoded?.street ?? '') +
                    ' ' +
                    (routePoint.address_geocoded?.house ?? '') +
                    ', ' +
                    (routePoint.address_geocoded?.city ?? '');
            } else {
                currentAddress = warehouse ? warehouse.name : 'Koniec';
            }

            // dodać czas postoju
            if (previousTime !== 0 && previousPoint && previousPoint.jobs) {
                routeDeliveryIterator++;
                let jobStopTime = routePoint.delta.g.t - routePoint.delta.t - previousTime; // minutes
                ganttAssignment.addAssignmentPart(
                    new AssignmentPart(
                        AssignmentType.Delivery,
                        jobStopTime, // minutes
                        previousAddress,
                        '',
                        '',
                        0,
                        previousPoint.jobs.length,
                        routeDeliveryIterator
                    )
                );
            }

            // dodać czas jazdy
            ganttAssignment.addAssignmentPart(
                new AssignmentPart(
                    AssignmentType.Drive,
                    routePoint.delta.t, // minutes
                    '',
                    previousAddress,
                    currentAddress,
                    routePoint.delta.d,
                    0,
                    0
                )
            );

            previousAddress = currentAddress;
            previousTime = routePoint.delta.g.t;
            previousPoint = routePoint;
        }

        return ganttAssignment;
    }

    function getGanttAssignmentForRoutePointsCurrent(
        assignmentsWithRoutePoint: AssignmentWithRoutePoints,
        reorderStack: ReorderedRouteType
    ): Assignment {
        let assignmentId = assignmentsWithRoutePoint.assignment.id;
        let assignment = assignmentsWithRoutePoint.assignment;
        let routePoints = assignmentsWithRoutePoint.routePoints;
        let vehicle = assignmentsWithRoutePoint.vehicle;
        let warehouse = assignmentsWithRoutePoint.warehouse;

        let previousAddress = warehouse ? warehouse.name : 'Start';
        let previousTime = 0;
        let previousPoint: any = null;
        let ganttAssignment = new Assignment(
            assignmentId,
            vehicle.name,
            assignment.color || '#000000',
            reorderStack?.start_time ?? assignment.start_time_route,
            GanttCouriersSettingsModeType.CURRENT
        );

        let routeDeliveryIterator = 1;

        if (reorderStack.deltas === null) {
            console.log('Skipping assignment, no deltas', assignmentId);
            return ganttAssignment;
        }
        if (typeof reorderStack.deltas === 'undefined') {
            console.log('Skipping assignment, no deltas', assignmentId);
            return ganttAssignment;
        }

        // during route optimization delta of all points is null, normal route has only first point delta set to null
        if (!routePoints[1] || routePoints[1].delta === null) {
            console.log('Skipping assignment, no delta', assignmentId);
            return ganttAssignment;
        }

        // sort reorderStack.deltas by g.t ascending
        let deltasIdsSorted = ReorderStackUtils.sortDeltasByTime(reorderStack);

        // console.log('gantt-current reorder stack', reorderStack);
        // console.log('gantt-current routePoints', routePoints);

        deltasIdsSorted.forEach(key => {
            let routePoint: RoutePointDeltaType | null = reorderStack.deltas[key] ?? null;
            if (routePoint === null) {
                return;
            }

            let currentAddress = '';
            let routePoint2: RoutePointExtendedType | null =
                routePoints.find(e => e.id.toString() === key) ?? null;
            if (routePoint2 && routePoint2.jobs) {
                currentAddress =
                    (routePoint2.address_geocoded?.street ?? '') +
                    ' ' +
                    (routePoint2.address_geocoded?.house ?? '') +
                    ', ' +
                    (routePoint2.address_geocoded?.city ?? '');
            } else {
                currentAddress = warehouse ? warehouse.name : 'Koniec';
            }

            // dodać czas postoju
            let jobStopTime = 0;
            if (previousTime !== 0 && previousPoint) {
                jobStopTime =
                    ganttCouriersSettings.eachStopTimeMinutes +
                    ganttCouriersSettings.eachDeliveryTimeMinutes;
                routeDeliveryIterator++;
                ganttAssignment.addAssignmentPart(
                    new AssignmentPart(
                        AssignmentType.Delivery,
                        jobStopTime, // minutes
                        previousAddress,
                        '',
                        '',
                        0,
                        1,
                        routeDeliveryIterator
                    )
                );
                // console.log('gantt-current Delivery_', key, AssignmentType.Delivery, jobStopTime);
            }

            let jobDriveTime = routePoint.g.t - previousTime - jobStopTime; // minutes

            if (jobDriveTime > 0) {
                // dodać czas jazdy
                ganttAssignment.addAssignmentPart(
                    new AssignmentPart(
                        AssignmentType.Drive,
                        jobDriveTime, // minutes
                        '',
                        previousAddress,
                        currentAddress,
                        routePoint.d,
                        0,
                        0
                    )
                );
            }

            // console.log('gantt-current Drive____', key, AssignmentType.Drive, jobDriveTime);

            previousAddress = currentAddress;
            previousPoint = routePoint;
            previousTime = routePoint.g.t;
        });

        return ganttAssignment;
    }

    function getGanttAssignmentForRoutePointsApproached(
        assignmentsWithRoutePoint: AssignmentWithRoutePoints
    ): Assignment {
        let assignmentId = assignmentsWithRoutePoint.assignment.id;
        let assignment = assignmentsWithRoutePoint.assignment;
        let routePoints = assignmentsWithRoutePoint.routePoints;
        let vehicle = assignmentsWithRoutePoint.vehicle;
        let warehouse = assignmentsWithRoutePoint.warehouse;

        // console.log(
        //     'gantt-current getGanttAssignmentForRoutePointsApproached, routePoints',
        //     routePoints
        // );

        // console.log('routePoints3, assignment.start_time_route, 1', assignment.start_time_route); // 2025-05-20 09:00:00
        let timestampStartTime = new Date(assignment.start_time_route);

        // get first approached timestamp from routePoints if exists
        let firstApproachedTimestamp = routePoints
            .filter(routePoint => routePoint.details && routePoint.details.approached)
            .map(routePoint => routePoint.details.approached)
            .sort((a, b) => a - b)[0];
        timestampStartTime = firstApproachedTimestamp
            ? new Date(firstApproachedTimestamp * 1000)
            : timestampStartTime;

        // set full hour -> 05:39 -> 05:00
        timestampStartTime.setMinutes(0);

        // format to YYYY-MM-DD HH:mm:ss
        assignment.start_time_route = `${timestampStartTime.getFullYear()}-${(
            timestampStartTime.getMonth() + 1
        )
            .toString()
            .padStart(2, '0')}-${timestampStartTime
            .getDate()
            .toString()
            .padStart(2, '0')} ${timestampStartTime
            .getHours()
            .toString()
            .padStart(2, '0')}:${timestampStartTime
            .getMinutes()
            .toString()
            .padStart(2, '0')}:${timestampStartTime.getSeconds().toString().padStart(2, '0')}`;
        // console.log('routePoints3, assignment.start_time_route, 2', assignment.start_time_route);

        // console.log('routePoints3 timestampStartTime', assignmentId, timestampStartTime.getTime());

        // approach from routePoint ----- 1747754384
        // sdtart getTime()-------------- 1747762683
        // end getTime()----------------- 1790957437

        let timestampEndTime = new Date(timestampStartTime.getTime() + 3600 * 1000 * 12);

        // console.log('routePoints3 timestampEndTime', assignmentId, timestampEndTime.getTime());

        // console.log('routePoints3 count total1', assignmentId, routePoints.length, routePoints);

        // get routePoints only with details.approached not null and sort by details.approached timestamp
        routePoints = routePoints
            .filter(routePoint => {
                return (
                    routePoint.details &&
                    routePoint.details.approached &&
                    routePoint.details.approached !== null
                );
            })
            .sort((a, b) => {
                return a.details.approached - b.details.approached;
            });

        // console.log('routePoints3 count total2', assignmentId, routePoints.length);

        // remove routePoints with details.approached before start_time
        routePoints = routePoints.filter(routePoint => {
            return routePoint.details.approached * 1000 >= timestampStartTime.getTime();
        });

        // console.log('routePoints3 count total3', assignmentId, routePoints.length);

        // remove routePoints with details.approached after start_time + 10 hours

        routePoints = routePoints.filter(routePoint => {
            return routePoint.details.approached * 1000 <= timestampEndTime.getTime();
        });

        // console.log('routePoints3 count total4', assignmentId, routePoints.length);

        let previousAddress = warehouse ? warehouse.name : 'Start';
        let previousTime = 0;
        let previousPoint = null;
        let ganttAssignment = new Assignment(
            assignmentId,
            vehicle.name,
            assignment.color || '#000000',
            assignment.start_time_route, // string ex. 08:00
            GanttCouriersSettingsModeType.APPROACHED
        );

        let routeDeliveryIterator = 1;

        const jobStopTime =
            ganttCouriersSettings.eachStopTimeMinutes +
            ganttCouriersSettings.eachDeliveryTimeMinutes; // minutes

        // during route optimization delta of all points is null, normal route has only first point delta set to null
        if (routePoints.length === 0) {
            console.log('Skipping assignment, no points', assignmentId);
            return ganttAssignment;
        }
        if (routePoints.length > 0 && routePoints[1] && routePoints[1].delta === null) {
            console.log('Skipping assignment, single point but with delta null', assignmentId);
            return ganttAssignment;
        }

        for (let routePointIterator in routePoints) {
            let routePoint = routePoints[routePointIterator];
            if (routePoint.delta === null) {
                continue;
            }

            let currentAddress = '';
            if (routePoint.jobs) {
                currentAddress =
                    (routePoint.address_geocoded?.street ?? '') +
                    ' ' +
                    (routePoint.address_geocoded?.house ?? '') +
                    ', ' +
                    (routePoint.address_geocoded?.city ?? '');
            } else {
                currentAddress = warehouse ? warehouse.name : 'Koniec';
            }

            // dodać czas postoju
            if (previousTime !== 0 && previousPoint && previousPoint.jobs) {
                routeDeliveryIterator++;
                // console.log('routePoints3, add jobStopTime', jobStopTime);
                ganttAssignment.addAssignmentPart(
                    new AssignmentPart(
                        AssignmentType.Delivery,
                        jobStopTime, // minutes
                        previousAddress,
                        '',
                        '',
                        0,
                        previousPoint.jobs.length,
                        routeDeliveryIterator
                    )
                );
            }

            // console.log('routePoints3, routePoint.id, ', routePoint.id);
            // console.log(
            //     'routePoints3, routePoint.details.approached',
            //     routePoint.details.approached
            // );

            if (routePoint.details.approached) {
                let jobDriveTime = Math.max(
                    0,
                    (routePoint.details.approached - timestampStartTime.getTime() / 1000) / 60 -
                        previousTime -
                        jobStopTime
                ); // minutes
                //
                // console.log(
                //     'routePoints3, add jobDriveTime',
                //     jobDriveTime,
                //     routePoint.details.approached - timestampStartTime.getTime() / 1000
                // );

                // dodać czas jazdy
                ganttAssignment.addAssignmentPart(
                    new AssignmentPart(
                        AssignmentType.Drive,
                        jobDriveTime, // minutes
                        '',
                        previousAddress,
                        currentAddress,
                        routePoint.delta.d,
                        0,
                        0
                    )
                );
            }

            previousAddress = currentAddress;
            if (routePoint.details.approached) {
                previousTime =
                    (routePoint.details.approached - timestampStartTime.getTime() / 1000) / 60;
            }
            // console.log('routePoints3, add previousTime', previousTime);
            previousPoint = routePoint;
        }

        // Add final delivery/stop time if the last processed point was a job
        // https://er.automapa.pl/issues/45722
        if (
            previousPoint &&
            previousPoint.jobs &&
            previousPoint.details &&
            previousPoint.details.approached
        ) {
            routeDeliveryIterator++;
            ganttAssignment.addAssignmentPart(
                new AssignmentPart(
                    AssignmentType.Delivery,
                    jobStopTime, // The fixed stop time used throughout this function
                    previousAddress, // Address of the last point
                    '',
                    '',
                    0,
                    previousPoint.jobs.length, // Number of jobs at the last stop
                    routeDeliveryIterator
                )
            );
            // console.log(
            //     'routePoints3, add FINAL jobStopTime for last point:',
            //     jobStopTime,
            //     previousPoint.id
            // );
        }

        return ganttAssignment;
    }

    function processData() {
        console.log('Processing data...');
        let localAssignmentsData = new AssignmentsData();
        assignmentsWithRoutePoints.sort((a, b) => a.assignment.id - b.assignment.id);
        for (let assignmentsWithRoutePoint of assignmentsWithRoutePoints) {
            console.log('assignmentsWithRoutePoint', assignmentsWithRoutePoint);

            let ganttAssignment: Assignment;
            switch (ganttCouriersSettings.dataMode) {
                case GanttCouriersSettingsModeType.APPROACHED:
                    ganttAssignment =
                        getGanttAssignmentForRoutePointsApproached(assignmentsWithRoutePoint);
                    break;
                case GanttCouriersSettingsModeType.PLANNED:
                    ganttAssignment =
                        getGanttAssignmentForRoutePointsPlanned(assignmentsWithRoutePoint);
                    break;
                case GanttCouriersSettingsModeType.CURRENT:
                    if (ganttCouriersSettings.useDebugData) {
                        let reorderStack = { ...reorderStackFile }; // TODO: remove this from production
                        ganttAssignment = getGanttAssignmentForRoutePointsCurrent(
                            assignmentsWithRoutePoint,
                            reorderStack
                        );
                    } else if (
                        assignmentsWithRoutePoint.assignment.route_info &&
                        assignmentsWithRoutePoint.assignment.route_info?.reorder_stack &&
                        assignmentsWithRoutePoint.assignment.route_info.reorder_stack?.length > 0
                    ) {
                        let reorderStack =
                            assignmentsWithRoutePoint.assignment.route_info.reorder_stack[
                                assignmentsWithRoutePoint.assignment.route_info.reorder_stack
                                    .length - 1
                            ];
                        ganttAssignment = getGanttAssignmentForRoutePointsCurrent(
                            assignmentsWithRoutePoint,
                            reorderStack
                        );
                    } else {
                        ganttAssignment =
                            getGanttAssignmentForRoutePointsPlanned(assignmentsWithRoutePoint);
                    }
                    break;
                default:
                    throw new Error('Invalid data mode');
            }

            ganttAssignment.cost =
                RouteInfoUtils.getCostByDistanceAndQuantity(
                    ganttAssignment.totalDistance,
                    ganttAssignment.stops,
                    ganttAssignment.totalTime * 60
                ) ?? 0;

            localAssignmentsData.addAssignment(ganttAssignment);
        }
        assignmentsData = localAssignmentsData;

        recalculateHoursHeader();
    }

    function getTotalTimelineWidth(totalMinutes: number): number {
        let totalTimelineWidth = 0;
        let timelineContainers = document.querySelectorAll(
            '.timeline-container'
        ) as NodeListOf<HTMLDivElement>;
        if (timelineContainers.length === 0) {
            console.log('totalTimelineWidth, timelineContainers is empty array');
            return 0;
        }
        for (let timelineContainer of timelineContainers) {
            if (timelineContainer) {
                // hide (but not remove) childs of this element to get correct width
                if (timelineContainer?.firstChild && false) {
                    timelineContainer.firstChild.style.display = 'none';
                    timelineContainer.firstChild.style.width = '0';
                }
                let columnWidthPx = timelineContainer?.getBoundingClientRect().width || null;
                console.log('totalTimelineWidth, columnWidthPx=', columnWidthPx);
                if (columnWidthPx) {
                    ganttCouriersSettings.setPixelsPerMinute(columnWidthPx / totalMinutes, true);
                } else {
                    ganttCouriersSettings.setPixelsPerMinute(1, true);
                }
                totalTimelineWidth = totalMinutes * ganttCouriersSettings.pixelsPerMinute;
                if (timelineContainer?.firstChild && false) {
                    timelineContainer.firstChild.style.display = 'flex';
                    timelineContainer.firstChild.style.width = totalTimelineWidth + 'px';
                }
            } else {
                console.log('totalTimelineWidth, columnWidthPx, timelineContainer is null');
            }
        }
        return totalTimelineWidth;
    }

    function recalculateHoursHeader() {
        if (assignmentsData.assignments.length === 0) {
            console.log('No assignments available');
            timeRange = null;
            earliestStartTime = null;
            earliestStartTimeRounded = null;
            totalTimelineWidth = 0;
            return;
        }

        if (ganttCouriersSettings.useRealTime) {
            try {
                // Real-time mode - use actual start times
                let earliestStartTimeDate = new Date(assignmentsData.assignments[0].startTime);
                console.log(
                    'routePoint3, earliestStartTimeDate',
                    earliestStartTimeDate,
                    assignmentsData.assignments[0].startTime
                );
                let latestEndTime = new Date(assignmentsData.assignments[0].startTime);
                latestEndTime.setMinutes(
                    latestEndTime.getMinutes() + assignmentsData.assignments[0].totalTime
                );

                for (let i = 1; i < assignmentsData.assignments.length; i++) {
                    const assignment = assignmentsData.assignments[i];
                    const startTime = new Date(assignment.startTime);
                    const endTime = new Date(assignment.startTime);
                    endTime.setMinutes(endTime.getMinutes() + assignment.totalTime);

                    if (startTime < earliestStartTimeDate) {
                        earliestStartTimeDate = startTime;
                    }
                    if (endTime > latestEndTime) {
                        latestEndTime = endTime;
                    }
                }

                earliestStartTime = earliestStartTimeDate.toISOString();
                earliestStartTimeDate.setMinutes(0);
                earliestStartTimeDate.setSeconds(0);
                earliestStartTimeDate.setMilliseconds(0);
                earliestStartTimeRounded = earliestStartTimeDate.toISOString();

                const endMinutes = latestEndTime.getMinutes();
                const endSeconds = latestEndTime.getSeconds();
                const endMilliseconds = latestEndTime.getMilliseconds();

                if (endMinutes !== 0 || endSeconds !== 0 || endMilliseconds !== 0) {
                    latestEndTime.setMinutes(0);
                    latestEndTime.setSeconds(0);
                    latestEndTime.setMilliseconds(0);
                    latestEndTime.setHours(latestEndTime.getHours() + 1);
                }

                const startTime = formatHourMinute(earliestStartTimeDate.toISOString());
                const endTime = formatHourMinute(latestEndTime.toISOString());

                // Calculate total timeline width
                const totalMinutes =
                    (latestEndTime.getTime() - earliestStartTimeDate.getTime()) / (60 * 1000);
                totalTimelineWidth = getTotalTimelineWidth(totalMinutes);

                // Generate hour markers with positions
                const hours: { time: string; position: number }[] = [];
                let currentTime = new Date(earliestStartTimeDate);

                while (currentTime <= latestEndTime) {
                    const minutesFromStart =
                        (currentTime.getTime() - earliestStartTimeDate.getTime()) / (60 * 1000);
                    const position = minutesFromStart * ganttCouriersSettings.pixelsPerMinute;
                    hours.push({
                        time: formatHourMinute(currentTime.toISOString()),
                        position: position,
                    });
                    currentTime.setHours(currentTime.getHours() + 1);
                }

                console.log('Calculated timeRange (real-time):', {
                    start: startTime,
                    end: endTime,
                    hours,
                    totalMinutes,
                    totalTimelineWidth,
                    pixelsPerMinute: ganttCouriersSettings.pixelsPerMinute,
                    earliestStartTime: earliestStartTimeDate.toISOString(),
                    latestEndTime: latestEndTime.toISOString(),
                });
                timeRange = { start: startTime, end: endTime, hours };
            } catch (error) {
                console.error('Error calculating time range:', error);
                timeRange = null;
            }
        } else {
            try {
                // Relativistic mode - all assignments start at 00:00
                let maxDuration = assignmentsData.assignments[0].totalTime;
                for (let i = 1; i < assignmentsData.assignments.length; i++) {
                    if (assignmentsData.assignments[i].totalTime > maxDuration) {
                        maxDuration = assignmentsData.assignments[i].totalTime;
                    }
                }

                const baseDate = new Date(assignmentsData.assignments[0].startTime);
                baseDate.setHours(0, 0, 0, 0);
                earliestStartTime = baseDate.toISOString();
                earliestStartTimeRounded = baseDate.toISOString();

                const endDate = new Date(baseDate);
                endDate.setMinutes(endDate.getMinutes() + maxDuration);

                const endMinutes = endDate.getMinutes();
                const endSeconds = endDate.getSeconds();
                const endMilliseconds = endDate.getMilliseconds();

                if (endMinutes !== 0 || endSeconds !== 0 || endMilliseconds !== 0) {
                    endDate.setMinutes(0);
                    endDate.setSeconds(0);
                    endDate.setMilliseconds(0);
                    endDate.setHours(endDate.getHours() + 1);
                }

                const startTime = '00:00';
                const endTime = formatHourMinute(endDate.toISOString());

                const totalMinutes = (endDate.getTime() - baseDate.getTime()) / (60 * 1000);
                totalTimelineWidth = getTotalTimelineWidth(totalMinutes);

                const hours: { time: string; position: number }[] = [];
                let currentTime = new Date(baseDate);

                while (currentTime <= endDate) {
                    const minutesFromStart =
                        (currentTime.getTime() - baseDate.getTime()) / (60 * 1000);
                    const position = minutesFromStart * ganttCouriersSettings.pixelsPerMinute;
                    hours.push({
                        time: formatHourMinute(currentTime.toISOString()),
                        position: position,
                    });
                    currentTime.setHours(currentTime.getHours() + 1);
                }

                console.log('Calculated timeRange (relativistic):', {
                    start: startTime,
                    end: endTime,
                    hours,
                    totalMinutes,
                    totalTimelineWidth,
                    pixelsPerMinute: ganttCouriersSettings.pixelsPerMinute,
                    earliestStartTime: baseDate.toISOString(),
                    latestEndTime: endDate.toISOString(),
                });
                timeRange = { start: startTime, end: endTime, hours };
            } catch (error) {
                console.error('Error calculating time range:', error);
                timeRange = null;
            }
        }
    }

    // // Format time in minutes:seconds
    // function formatTime(seconds: number): string {
    //     if (seconds === undefined) return '';
    //     return getHumanFriendlyTimeDuration(seconds);
    //     // if (seconds === undefined) return '';
    //     // const minutes = Math.floor(seconds / 60);
    //     // const remainingSeconds = Math.floor(seconds % 60);
    //     // return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    // }

    // Format time in HH:MM
    function formatHourMinute(dateTimeStr: string): string {
        const date = new Date(dateTimeStr);
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    }

    function getHoursBetween(
        startTime: string,
        endTime: string
    ): { time: string; position: number }[] {
        // Parse hours from time strings (format: "HH:MM")
        const startHour = parseInt(startTime.split(':')[0]);
        const endHour = parseInt(endTime.split(':')[0]);
        const hours: { time: string; position: number }[] = [];

        // Generate array of hours with positions
        for (let hour = startHour; hour <= endHour; hour++) {
            const minutesFromStart = (hour - startHour) * 60;
            const position = minutesFromStart * ganttCouriersSettings.pixelsPerMinute;
            hours.push({
                time: `${hour.toString().padStart(2, '0')}:00`,
                position: position,
            });
        }

        return hours;
    }

    async function load() {
        try {
            isLoading = true;
            console.log('GanttCouriers, load');
            cleanup();
            await loadAssignments();
            await loadVehicles();
            await loadWarehouses();
            await loadRoutes();

            await tick();
            ganttCouriersSettings.tickCounter(false);
            ganttCouriersSettings = ganttCouriersSettings;
            recalculateHoursHeader();
        } catch (err) {
            console.error('PageGanttCouriers, load, error', err);
            error = 'Failed to load data';
        } finally {
            isLoading = false;
        }
    }

    afterUpdate(() => {
        ganttCouriersSettings.tickCounter(false);
        ganttCouriersSettings = ganttCouriersSettings;
        recalculateHoursHeader();
    });

    onMount(() => {
        if (
            (!ganttCouriersSettings.assignmentIdsLoaded || ganttCouriersSettings.useDumpApi) &&
            !isLoading
        ) {
            console.log('GanttCouriers, onMount, loading data');
            (async () => {
                await load();
            })();
        }
        return () => {
            console.log('GanttCouriers, onMount, cleanup');
            cleanup();
        };
        // load();
        // ganttCouriersSettings.tickCounter();
        // ganttCouriersSettings = ganttCouriersSettings;
    });

    $: {
        if (ganttCouriersSettings && !error) {
            console.log('GanttCouriers, ganttCouriersSettings changed', ganttCouriersSettings);
            recalculateHoursHeader();
        }
    }

    // $: { // not works when width is decreasing
    //     if ($leftPageWidthPx >= 0) {
    //         console.log('GanttCouriers, leftPageWidthPx changed', ganttCouriersSettings);
    //         ganttCouriersSettings.tickCounter(false);
    //         ganttCouriersSettings = ganttCouriersSettings;
    //         recalculateHoursHeader();
    //     }
    // }

    $: {
        if (
            ganttCouriersSettings &&
            ganttCouriersSettings.assignmentIdsLoaded === false &&
            !isLoading &&
            !error
        ) {
            console.log(
                'GanttCouriers, ganttCouriersSettings.assignmentIdsLoaded changed',
                ganttCouriersSettings
            );
            load();
        }
    }

    let modeTypeDescription = '';
    $: {
        if (!error) {
            if (ganttCouriersSettings.dataMode === GanttCouriersSettingsModeType.APPROACHED) {
                modeTypeDescription = 'Approached';
            } else if (ganttCouriersSettings.dataMode === GanttCouriersSettingsModeType.PLANNED) {
                modeTypeDescription = 'Planned';
            } else if (ganttCouriersSettings.dataMode === GanttCouriersSettingsModeType.CURRENT) {
                modeTypeDescription = 'Current';
            } else {
                modeTypeDescription = 'Unknown';
            }
        }
    }
</script>

<div class="gantt-container">
    {#if isLoading}
        <div style="display: flex; align-items: center; justify-content: center;">
            <!--            <TemplateInner>-->
            <!--            <MessageInline text={loadingMessage} />-->
            <Spinner />
            <!--            </TemplateInner>-->
        </div>
    {:else if error}
        <TemplateInner>
            <ErrorInline text={error ?? $_('glo_something_went_wrong')} />
            <ButtonSmallBlue
                text={$_('try_again')}
                on:click={() => {
                    error = '';
                    load();
                }}
            />
        </TemplateInner>
    {:else if assignmentsData.assignments.length === 0}
        {#if $debug || true}
            <InfoInline text={$_('gantt-warning-no-assignments')} />
        {/if}
    {:else}
        <div class="gantt-container-inner">
            {#if $debug}
                <div>
                    <ButtonSmallBlue
                        text={$_('gantt-refresh')}
                        on:click={() => {
                            ganttCouriersSettings.tickCounter(true);
                            ganttCouriersSettings = ganttCouriersSettings;
                            load();
                        }}
                    />
                </div>
            {/if}
            <table class="gantt-table">
                <thead>
                    <tr>
                        {#if ganttCouriersSettings.visibleDay || $debug}
                            <th class="col-id">
                                <span>{$_('day')}</span>
                            </th>
                        {/if}
                        {#if ganttCouriersSettings.visibleId || $debug}
                            <th class="col-id">
                                <span
                                    >{assignmentsData.assignments.length}*{$_(
                                        'gantt-table-header-k'
                                    )}</span
                                >
                            </th>
                        {/if}
                        {#if ganttCouriersSettings.visibleTime}
                            <th class="col-time">
                                <span class="label">{$_('gantt-table-header-time')}</span>
                                <span class="value">{formatTime(assignmentsData.totalTime)}</span>
                            </th>
                        {/if}
                        {#if ganttCouriersSettings.visibleParcels}
                            <th class="col-pkg">
                                <span class="label">{$_('gantt-table-header-pkg')}</span>
                                <span class="value"
                                    >{new smartRounding().getFormatedValue(
                                        assignmentsData.totalPackages,
                                        '',
                                        { convert_nicer: true }
                                    )}</span
                                >
                            </th>
                        {/if}
                        {#if ganttCouriersSettings.visibleStops}
                            <th class="col-stops">
                                <span class="label">{$_('gantt-table-header-stops')}</span>
                                <span class="value"
                                    >{new smartRounding().getFormatedValue(
                                        assignmentsData.totalStops,
                                        '',
                                        { convert_nicer: true }
                                    )}</span
                                >
                            </th>
                        {/if}
                        {#if ganttCouriersSettings.visibleDistance}
                            <th class="col-distance">
                                <span class="label">{$_('gantt-table-header-distance')}</span>
                                <span class="value"
                                    >{new smartRounding().getFormatedValue(
                                        assignmentsData.totalDistance / 1000,
                                        'km',
                                        { convert_nicer: false }
                                    )}</span
                                >
                            </th>
                        {/if}
                        {#if ganttCouriersSettings.visibleCost}
                            <th class="col-cost">
                                <span class="label">{$_('gantt-table-header-cost')}</span>
                                <span
                                    class="value"
                                    title="{assignmentsData.totalCost.toFixed(
                                        2
                                    )} {RouteInfoUtils.getCurrency()}"
                                >
                                    {new smartRounding().getFormatedValue(
                                        assignmentsData.totalCost,
                                        RouteInfoUtils.getCurrency(),
                                        { convert_nicer: false }
                                    )}
                                    <!--{assignmentsData.totalCost.toFixed(0)}-->
                                    <!--{RouteInfoUtils.getCurrency()}-->
                                </span>
                            </th>
                        {/if}
                        {#if ganttCouriersSettings.visiblePercents}
                            <th class="col-time-of-drive">
                                <table class="time-of-table">
                                    <tr>
                                        <td class="time-of-cell time-of-cell-left">
                                            <div class="time-of-cell-inner">
                                                <div
                                                    class="time-of-corner time-of-corner-left"
                                                ></div>
                                            </div>
                                        </td>
                                        <td class="time-of-label">
                                            <span title={$_(modeTypeDescription)}>
                                                {$_('gantt-table-header-time-of')}
                                            </span>
                                        </td>
                                        <td class="time-of-cell time-of-cell-right">
                                            <div class="time-of-cell-inner">
                                                <div
                                                    class="time-of-corner time-of-corner-right"
                                                ></div>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>{$_('gantt-table-header-drive')}</td>
                                        <td></td>
                                        <td>{$_('gantt-table-header-delivery')}</td>
                                    </tr>
                                </table>
                            </th>
                        {/if}
                        <!--                    <th class="col-time-of-delivery"> </th>-->
                        {#if ganttCouriersSettings.visibleTimeLine}
                            <th class="col-timeline" style="height: 42px;">
                                {#if timeRange}
                                    <div class="timeline-time-header">
                                        <div class="timeline-hours">
                                            {#each timeRange.hours as hour}
                                                <span
                                                    class="hour-marker"
                                                    style="left: {hour.position}px"
                                                    >{hour.time}</span
                                                >
                                            {/each}
                                        </div>
                                    </div>
                                {/if}
                            </th>
                        {/if}
                    </tr>
                </thead>
                <tbody>
                    {#each assignmentsData.assignments as assignment}
                        <PageGanttCouriersRow
                            {assignment}
                            chartStartTime={earliestStartTimeRounded || assignment.startTime}
                            {totalTimelineWidth}
                            {ganttCouriersSettings}
                        />
                    {/each}
                </tbody>
            </table>
        </div>
    {/if}
</div>

<style>
    .gantt-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 15px 0;
        padding: 0 10px;
    }

    .switch input {
        opacity: 0;
        width: 0;
        height: 0;
    }

    .gantt-container {
        box-sizing: border-box;
        display: flex;
        justify-content: center;
        /*align-items: center;*/
        width: 100%;
        min-height: 100px;
        margin-bottom: 20px;
    }

    .gantt-container-inner {
        box-sizing: border-box;
        border-radius: 4px;
        /*width: 95%;*/
        width: 100%;
        min-height: 86px;
        display: flex;
        align-items: flex-start;
        justify-content: flex-start;
        /*max-height: 155px;*/
        /*overflow-y: auto;*/
        overflow-x: visible;
        overflow-y: scroll;
        /* Niestandardowe style dla cienkiego paska przewijania */
        scrollbar-width: thin; /* Firefox */
        scrollbar-color: rgba(155, 155, 155, 0.7) transparent; /* Firefox */
        padding-bottom: 10px; /* Dodatkowa przestrzeń na pasek przewijania */
    }

    /* Style dla cienkiego paska przewijania w WebKit (Chrome, Safari, Edge) */
    .gantt-container-inner::-webkit-scrollbar {
        width: 6px; /* Szerokość paska przewijania */
    }

    .gantt-container-inner::-webkit-scrollbar-track {
        background: transparent; /* Tło ścieżki paska przewijania */
    }

    .gantt-container-inner::-webkit-scrollbar-thumb {
        background-color: rgba(155, 155, 155, 0.7); /* Kolor paska przewijania */
        border-radius: 3px; /* Zaokrąglenie rogów paska przewijania */
    }

    .gantt-container-inner::-webkit-scrollbar-thumb:hover {
        background-color: rgba(125, 125, 125, 0.8); /* Kolor paska przewijania przy najechaniu */
    }

    .gantt-table {
        border-collapse: collapse;
        /*table-layout: fixed;*/
        width: 100%;
    }

    /*.gantt-table tbody {*/
    /*    max-height: 100px;*/
    /*    overflow-y: auto;*/
    /*    overflow-x: hidden;*/
    /*}*/

    .gantt-table th {
        box-sizing: border-box;
        text-align: right;
        vertical-align: bottom;
        font-weight: normal;
        padding-left: 3px;
        padding-right: 3px;
        border-bottom: 2px solid #c7c7c7;
    }

    th.col-time-of-drive {
        color: #5f5f5f;
    }

    th.col-timeline {
        position: relative;
        padding: 0;
        border-bottom: 0;
    }

    .label {
        color: #5f5f5f;
        display: block;
        font-size: 12px;
        margin-bottom: 2px;
        text-wrap: nowrap;
    }

    .value {
        font-weight: bold;
        font-size: 15px;
        text-wrap: nowrap;
    }

    .timeline-time-header {
        position: absolute;
        top: 5px;
        padding: 0 15px;
        font-size: 12px;
        color: #5f5f5f;
    }

    .timeline-hours {
        position: relative;
    }

    .hour-marker {
        position: absolute;
        transform: translateX(-50%);
        padding: 0 10px;
    }

    .hour-marker::after {
        content: '';
        position: absolute;
        bottom: -15px;
        left: 50%;
        width: 1px;
        height: 5px;
        background-color: #5f5f5f;
        transform: translateX(-50%);
    }

    .time-of-cell {
        position: relative;
    }

    .time-of-cell-inner {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 100%;
    }

    .time-of-corner {
        position: absolute;
        bottom: 0;
        width: 50%;
        height: 50%;
        border-top: 1px solid #5f5f5f;
    }

    .time-of-corner-left {
        right: 0;
        border-left: 1px solid #5f5f5f;
    }

    .time-of-corner-right {
        left: 0;
        border-right: 1px solid #5f5f5f;
    }

    .time-of-label {
        white-space: nowrap;
    }
</style>
